import React, { useEffect, useMemo, useState } from "react";

const DEFAULT_PLACEMENT = "login_mobile";

export default function SponsorBanner({
  placement = DEFAULT_PLACEMENT,
  label = "TeamKitty community sponsor",
  compact = false,
  className = ""
}) {
  const [sponsor, setSponsor] = useState(null);
  const [loading, setLoading] = useState(true);

  const baseUrl = useMemo(() => {
    return (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSponsor() {
      try {
        if (!baseUrl) {
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${baseUrl}/public/sponsor?placement=${encodeURIComponent(placement)}`
        );

        if (!response.ok) {
          throw new Error(`Sponsor fetch failed: ${response.status}`);
        }

        const data = await response.json();

        if (!cancelled) {
          setSponsor(data?.sponsor || null);
        }
      } catch (error) {
        console.warn("Sponsor banner unavailable", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSponsor();

    return () => {
      cancelled = true;
    };
  }, [baseUrl, placement]);

  async function trackClick() {
    try {
      if (!baseUrl || !sponsor?.sponsorId) return;

      await fetch(`${baseUrl}/public/sponsor-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sponsorId: sponsor.sponsorId, placement })
      });
    } catch (error) {
      console.warn("Sponsor click tracking failed", error);
    }
  }

  if (loading || !sponsor) return null;

  const bannerContent = (
    <div
      className={`rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 p-4 shadow-sm hover:shadow-md transition ${className}`}
    >
      <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
        {label}
      </div>

      <div className={`flex ${compact ? "items-center" : "items-start"} gap-3`}>
        {sponsor.logoUrl && (
          <img
            src={sponsor.logoUrl}
            alt={`${sponsor.name} logo`}
            className={`${compact ? "h-10 w-10" : "h-14 w-14"} rounded-xl object-contain bg-white border border-gray-200 p-1 flex-shrink-0`}
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="font-bold text-gray-900 dark:text-white truncate">
            {sponsor.name}
          </div>

          {sponsor.tagline && (
            <div className={`${compact ? "text-xs" : "text-sm"} text-gray-600 dark:text-gray-300 leading-snug`}>
              {sponsor.tagline}
            </div>
          )}

          {sponsor.offerText && (
            <div className={`${compact ? "mt-1 text-xs" : "mt-2 text-sm"} font-semibold text-blue-700 dark:text-blue-300`}>
              {sponsor.offerText}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!sponsor.website) return bannerContent;

  return (
    <a
      href={sponsor.website}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={trackClick}
      className="block no-underline"
    >
      {bannerContent}
    </a>
  );
}
